import { Component, OnInit } from '@angular/core';

interface Testimonial {
  id: string;
  content: string;
  author: {
    name: string;
    position: string;
    company: string;
    image?: string;
  };
  rating: number; // 1-5
  tags: string[];
}

@Component({
  selector: 'app-testimonials',
  templateUrl: './testimonials.component.html',
  styleUrls: ['./testimonials.component.scss'],
  standalone: false
})
export class TestimonialsComponent implements OnInit {
  testimonials: Testimonial[] = [
    {
      id: 'testimonial-1',
      content: 'Zapphira.ai transformó nuestra atención al cliente. Las consultas se resuelven en segundos y nuestro equipo ahora puede enfocarse en tareas de mayor valor. La integración con nuestro sitio web fue sorprendentemente sencilla.',
      author: {
        name: 'María Rodríguez',
        position: 'Directora de Servicio al Cliente',
        company: 'TechSolutions',
        image: 'assets/images/testimonials/maria-rodriguez.jpg'
      },
      rating: 5,
      tags: ['Atención al cliente', 'Integración web']
    },
    {
      id: 'testimonial-2',
      content: 'La integración con WhatsApp nos permitió aumentar nuestras conversiones en un 35%. La capacidad de respuesta inmediata marca la diferencia en nuestro sector, especialmente durante las campañas de marketing donde el volumen de consultas se multiplica.',
      author: {
        name: 'Carlos Méndez',
        position: 'CEO',
        company: 'E-Commerce Internacional',
        image: 'assets/images/testimonials/carlos-mendez.jpg'
      },
      rating: 5,
      tags: ['WhatsApp', 'Conversiones', 'Campañas de marketing']
    },
    {
      id: 'testimonial-3',
      content: 'Como institución educativa, necesitábamos una solución para responder consultas sobre inscripciones y programas las 24 horas. Zapphira.ai no solo logró esto, sino que su capacidad para aprender de las interacciones mejoró notablemente la precisión de las respuestas.',
      author: {
        name: 'Ana Gómez',
        position: 'Directora de Admisiones',
        company: 'Universidad Tecnológica',
        image: 'assets/images/testimonials/ana-gomez.jpg'
      },
      rating: 4,
      tags: ['Educación', 'Aprendizaje automático']
    },
    {
      id: 'testimonial-4',
      content: 'Implementamos Zapphira.ai en nuestra clínica para gestionar citas y consultas frecuentes. El resultado ha sido una reducción del 40% en llamadas de gestión básica y nuestros pacientes valoran recibir confirmaciones instantáneas por WhatsApp.',
      author: {
        name: 'Javier Torres',
        position: 'Director Médico',
        company: 'Centro Médico Salud Integral',
        image: 'assets/images/testimonials/javier-torres.jpg'
      },
      rating: 5,
      tags: ['Sector salud', 'Gestión de citas']
    },
    {
      id: 'testimonial-5',
      content: 'El soporte multilingüe de Zapphira.ai nos ha permitido expandirnos a nuevos mercados sin contratar personal adicional. Ahora atendemos clientes en 4 idiomas diferentes con la misma calidad y rapidez.',
      author: {
        name: 'Laura Fernández',
        position: 'Directora de Expansión',
        company: 'Global Retail Group',
        image: 'assets/images/testimonials/laura-fernandez.jpg'
      },
      rating: 4,
      tags: ['Multilingüe', 'Expansión internacional']
    },
    {
      id: 'testimonial-6',
      content: 'Utilizamos Zapphira.ai para capacitar a nuestros nuevos empleados. El bot responde preguntas sobre políticas, procedimientos y sistemas internos, lo que ha acelerado significativamente el proceso de onboarding y reducido la carga sobre nuestro equipo de RRHH.',
      author: {
        name: 'Miguel Sánchez',
        position: 'Director de Recursos Humanos',
        company: 'Corporativo Industrial',
        image: 'assets/images/testimonials/miguel-sanchez.jpg'
      },
      rating: 5,
      tags: ['Recursos Humanos', 'Onboarding']
    }
  ];

  filteredTestimonials: Testimonial[] = [];
  selectedTags: string[] = [];
  activePage: number = 1;
  itemsPerPage: number = 3;
  allTags: string[] = [];

  ngOnInit(): void {
    // Inicialmente mostrar todos los testimonios
    this.filteredTestimonials = [...this.testimonials];
    
    // Extraer todos los tags únicos
    this.allTags = Array.from(new Set(
      this.testimonials.flatMap(testimonial => testimonial.tags)
    )).sort();
  }

  // Añade este método a la clase TestimonialsComponent
  getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('');
  }

  // Generar un array de estrellas para mostrar la calificación
  generateRatingArray(rating: number): number[] {
    return Array(5).fill(0).map((_, i) => i < rating ? 1 : 0);
  }

  // Filtrar testimonios por tags
  filterByTag(tag: string): void {
    if (this.selectedTags.includes(tag)) {
      this.selectedTags = this.selectedTags.filter(t => t !== tag);
    } else {
      this.selectedTags.push(tag);
    }

    if (this.selectedTags.length === 0) {
      this.filteredTestimonials = [...this.testimonials];
    } else {
      this.filteredTestimonials = this.testimonials.filter(testimonial => 
        testimonial.tags.some(tag => this.selectedTags.includes(tag))
      );
    }

    // Resetear a la primera página después de filtrar
    this.activePage = 1;
  }

  // Verificar si un tag está seleccionado
  isTagSelected(tag: string): boolean {
    return this.selectedTags.includes(tag);
  }

  // Cambiar de página
  changePage(pageNumber: number): void {
    this.activePage = pageNumber;
  }

  // Obtener testimonios para la página actual
  getCurrentPageTestimonials(): Testimonial[] {
    const startIndex = (this.activePage - 1) * this.itemsPerPage;
    return this.filteredTestimonials.slice(startIndex, startIndex + this.itemsPerPage);
  }

  // Obtener el número total de páginas
  get totalPages(): number[] {
    return Array(Math.ceil(this.filteredTestimonials.length / this.itemsPerPage))
      .fill(0)
      .map((_, i) => i + 1);
  }
}